import React, { useEffect, useRef } from 'react';

import * as d3 from 'd3';

export const ColorConfig = {
  blue: {
    line: "#56F5FF", grad: undefined, gradStr: ""
  },
  blueWithGrad: {
    line: "#56F5FF", grad: [
      { offset: "0%", color: "#56F5FF", opacity: 0.3 },
      { offset: "100%", color: "#80B2FF", opacity: 0 }
    ], gradStr: "linear-gradient(to bottom, #56F5FF4c 0%, #80B2FF00 100%)"
  },
  yellow: {
    line: "#FFD94D", grad: undefined, gradStr: ""
  },
  yellowWithGrad: {
    line: "#FFD94D", grad: [
      { offset: "0%", color: "#FFD94D", opacity: 0.3 },
      { offset: "100%", color: "#715F1D", opacity: 0 }
    ], gradStr: "linear-gradient(to bottom, #FFD94D4c 0%, #715F1D00 100%)"
  },
  green: {
    line: "#A1E496", grad: undefined, gradStr: ""
  },
  greenWithGrad: {
    line: "#A1E496", grad: [
      { offset: "0%", color: "#A1E496", opacity: 0.3 },
      { offset: "100%", color: "#41B52E", opacity: 0 }
    ], gradStr: "linear-gradient(to bottom, #A1E4964c 0%, #41B52E00 100%)"
  },
  blue2: {
    line: "#7AAFFF", grad: undefined, gradStr: ""
  },
  blue2WithGrad: {
    line: "#7AAFFF", grad: [
      { offset: "0%", color: "#7AAFFF", opacity: 0.3 },
      { offset: "100%", color: "#183F7B", opacity: 0 }
    ], gradStr: "linear-gradient(to bottom, #7AAFFF4c 0%, #183F7B00 100%)"
  },
}

// not working sloution for wrap:
// https://stackoverflow.com/questions/24784302/wrapping-text-in-d3
// related to: 
// https://stackoverflow.com/questions/75595621/safari-rending-svg-text-tspans-in-wrong-positions-when-using-a-rtl-language

// linesList: [lines1, lines2]
// dotsList: [dots1, dots2]
// lines1: [{ x: date, y: value }] 
// dots1: [{ x: date, y: value }] 
// gaps: [{ start: date, end: date }] 
// colorConfigList: [colorConfig]
// colorConfig: {line: "#FF00FF", grad: undefined | gradConfig}
// gradConfig: [
//   { offset: "0%", color: "#0000FF", opacity: 0.3 },
//   { offset: "100%", color: "#AAAAAA", opacity: 0 }
// ]
// line 2 will be on top of line 1
export function BriefLineChart({
  chartKey, className, width, height,
  gaps, linesList, dotsList, valueFormatter, colorConfigList,
  dateBound, valueBound, smooth, marginLeft
}) {
  const chartRef = useRef();

  useEffect(() => {
    // config
    const marginRight = 8; // search BriefLineChart-margin-right
    const marginTop = 8;
    const marginBottom = 8;
    const valueTickMarginLeft = 2;
    const dataXBound = dateBound;
    const dataYBound = valueBound;
    const lineWidth = 2;
    const padding = 2;
    const dotRadius = lineWidth * 2 / 3;

    chartRef.current.innerHTML = "";

    const svg = d3.select(chartRef.current)
      .append("svg").attr("viewBox", [0, 0, width, height])
      .attr("width", width)
      .attr("height", height);

    const defs = svg.append("defs");

    // arrow
    defs.append("marker")
      .attr("id", chartKey + "-arrowhead")
      .attr("fill", "#A0A1A2")
      .attr("refX", 0)
      .attr("refY", 4)
      .attr("markerWidth", 5)
      .attr("markerHeight", 8)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M2.78114 3.49997L0.452711 1.17155C0.257449 0.976284 0.257449 0.659702 0.452711 0.46444C0.647973 0.269178 0.964557 0.269178 1.15982 0.46444L4.3418 3.64642C4.53706 3.84168 4.53706 4.15826 4.3418 4.35353L1.15982 7.53551C0.964557 7.73077 0.647973 7.73077 0.452711 7.53551C0.257449 7.34025 0.257449 7.02366 0.452711 6.8284L2.78114 4.49997L0 4.5V3.50003L2.78114 3.49997Z");

    // dc pattern
    const pattern = defs.append("pattern")
      .attr("id", chartKey + "-chart-bg-pattern")
      .attr("width", 4.95)
      .attr("height", 10)
      .attr("patternUnits", "userSpaceOnUse")
      .attr("patternTransform", "rotate(45)");
    pattern.append("rect")
      .attr("fill", "#151515")
      .attr("width", 20)
      .attr("height", 10)
      .attr("x", 0)
      .attr("y", 0);
    pattern.append("line")
      .attr("stroke", "#7D7D7D")
      .attr("stroke-width", 0.8)
      .attr("y2", 10);

    // fill grad
    for (let j = 0; j < colorConfigList.length; j++) {
      const colorConfig = colorConfigList[j];
      if (colorConfig.grad !== undefined) {
        svg.append("linearGradient")
          .attr("id", `${chartKey}-chart-fill-gradient-${j}`)
          .attr("gradientUnits", "userSpaceOnUse")
          .attr("x1", "0%")
          .attr("y1", "0%")
          .attr("x2", "0%")
          .attr("y2", "100%")
          .selectAll("stop")
          .data(colorConfig.grad)
          .enter().append("stop")
          .attr("offset", function (d) { return d.offset; })
          .attr("stop-color", function (d) { return d.color; })
          .attr("stop-opacity", function (d) { return d.opacity; });
      }
    }

    const x = d3.scaleTime()
      .domain([dataXBound.min, dataXBound.max])
      .range([padding + marginLeft, width - marginRight - padding]);

    let yMin = dataYBound.min;
    const yMax = dataYBound.max;
    yMin = yMin - (yMax - yMin) / 5;
    const y = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height - marginBottom - padding, marginTop + padding]);

    // y-axis
    const linesWidth = width - marginLeft - marginRight;
    svg.append("g")
      .attr("transform", `translate(${marginLeft},0)`)
      .attr("color", "white")
      .call(d3.axisLeft(y)
        .tickSize(-linesWidth)
        .ticks(3).tickFormat(valueFormatter))
      .call(g => g.selectAll(".tick").select("line")
        .attr("stroke-width", 0.5)
        .attr("stroke-opacity", 0.3)
        .attr("stroke-dasharray", "2,2")
      )
      .call(g => g.selectAll(".tick").select("text")
        .attr("transform", `translate(${-valueTickMarginLeft},0)`)
        .attr("class", "y-text")
      ).call(g => g.select(".domain").remove());

    // x-axis
    svg.append("g")
      .attr("transform", `translate(0,${height - marginBottom})`)
      .attr("color", "#A0A1A2")
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .call(g => g.selectAll(".tick").remove())
      .call(g => g.select(".domain").remove())
    // .attr("marker-end", "url(#arrowhead)"));

    let line = d3.line()
      .x(d => x(d.x))
      .y(d => y(d.y));
    let lineArea = d3.area()
      .x(d => {
        if (d.itsStart) {
          return x(d.x) - lineWidth / 2;
        } else if (d.itsEnd) {
          return x(d.x) + lineWidth / 2;
        } else {
          return x(d.x);
        }
      })
      .y0(() => height - marginBottom)
      .y1(d => y(d.y));
    
    if (smooth) {
      line = line.curve(d3.curveBasis)
      lineArea = lineArea.curve(d3.curveBasis)
    }

    // add lines' area
    for (let j = 0; j < linesList.length; j++) {
      const colorConfig = colorConfigList[j];
      if (colorConfig.grad !== undefined) {
        const lines = linesList[j];
        for (let i = 0; i < lines.length; i++) {
          const lineData = lines[i];
          svg.append("path")
            .datum(lineData)
            .attr("class", "area")
            .attr("d", lineArea)
            .attr("fill", `url(#${chartKey}-chart-fill-gradient-${j})`);
        }
      }
    }

    // add dots' area
    for (let j = 0; j < dotsList.length; j++) {
      const colorConfig = colorConfigList[j];
      if (colorConfig.grad !== undefined) {
        for (let i = 0; i < dotsList[j].length; i++) {
          const dot = dotsList[j][i];
          svg.append("rect")
            .attr("class", "area")
            .attr("x", x(dot.x) - dotRadius)
            .attr("y", y(dot.y))
            .attr("width", 2 * dotRadius)
            .attr("height", height - y(dot.y) - marginBottom)
            .attr("fill", `url(#${chartKey}-chart-fill-gradient-${j})`);
        }
      }
    }

    // add lines
    for (let j = 0; j < linesList.length; j++) {
      const lines = linesList[j];
      for (let i = 0; i < lines.length; i++) {
        const lineData = lines[i];
        svg.append("path")
          .datum(lineData)
          .attr("fill", "none")
          .attr("stroke", colorConfigList[j].line)
          .attr("stroke-width", lineWidth)
          .attr("stroke-linejoin", "round")
          .attr("stroke-linecap", "round")
          .attr("d", line);
      }
    }

    // add dots
    for (let j = 0; j < dotsList.length; j++) {
      svg.append('g')
        .selectAll("dot")
        .data(dotsList[j])
        .enter()
        .append("circle")
        .attr("cx", d => x(d.x))
        .attr("cy", d => y(d.y))
        .attr("r", dotRadius)
        .style("fill", colorConfigList[j].line)
    }

    // add gaps
    for (let i = 0; i < gaps.length; i++) {
      const gap = gaps[i];
      // padding is the space to the next/previous line
      const gapWidth = x(gap.end) - x(gap.start) - 2 * padding;
      if (gapWidth > 0)
        svg.append("rect")
          .attr("transform", `translate(${x(gap.start) + padding}, ${marginTop})`)
          .attr("width", gapWidth)
          .attr("height", height - marginTop - marginBottom)
          .attr("fill", "url(#" + chartKey + "-chart-bg-pattern)");
    }

    // x-axis-line
    svg.append("line")
      .attr("x1", marginLeft - 0.5) // 0.5 is half of this line's width
      .attr("x2", width - marginRight)
      .attr("y1", height - marginBottom)
      .attr("y2", height - marginBottom)
      .attr("stroke", "#A0A1A2")
      .attr("marker-end", `url(#${chartKey}-arrowhead)`);

    // y-axis-line
    svg.append("line")
      .attr("x1", marginLeft)
      .attr("x2", marginLeft)
      .attr("y1", height - marginBottom)
      .attr("y2", marginTop)
      .attr("stroke", "#A0A1A2")
      .attr("marker-end", `url(#${chartKey}-arrowhead)`);

  }, [chartKey, className, width, height, marginLeft,
    gaps, linesList, dotsList, valueFormatter, colorConfigList,
    dateBound, valueBound, smooth]);


  return (
    <div className={className} ref={chartRef} />
  );
}

/**
  const lineData1 = [
    27, 27, 26, 25, 26, 25, 24, 24, 23, 22,
    21, 20, 19, 19, 19, 18, 18, null, null, null,
    23, 23, 24, null, 25, null, 26, null, 26, 28
  ];
  const lineData2 = [
    23, 23, 23, 23, 23, 23, 23, 23, 23, 23,
    23, 23, 23, 23, 15, 15, 15, null, null, null,
    15, 15, 15, null, 15, null, 15, null, 15, 15
  ];
  const [dots1, lines1, gaps1, dateBound1, valueBound1] =
    prepareLineChartWithGap(new Date(), 1 * 60 * 1000, lineData1, null);
  const [dots2, lines2, _, __, valueBound2] = // eslint-disable-line no-unused-vars
    prepareLineChartWithGap(new Date(), 1 * 60 * 1000, lineData2, null);
  
  <BriefLineChart
    chartKey="playground-svg" className="playground-svg"
    width={400} height={150}
    gaps={gaps} linesList={[lines1, lines2]} dotsList={[dots1, dots2]}
    valueFormatter={(v) => v} colorConfigList={[
        ColorConfig.blueWithGrad,
        ColorConfig.yellow
    ]}
    dateBound={dateBound} valueBound={valueBound} smooth={true}
  />
*/